import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Dialog sizing.
 *
 * Fixed `w-dialog-*` tokens rather than shadcn's `sm:max-w-lg`, because W3 bans
 * `max-w-*` outright (dead gutters) — the preset carries the four widths for
 * exactly this.
 *
 * Every width is `sm:`-prefixed because below that breakpoint a dialog is not a
 * card at all: it is a bottom sheet spanning the viewport (see
 * `DIALOG_POSITION_CLASSES`), and a width there would only fight it.
 *
 * **A size is a CEILING, reached one breakpoint at a time.** `xl` used to mean a
 * flat `sm:w-dialog-xl`, i.e. 960px from 640px up — so on a 768px tablet the
 * dialog hung 96px off each edge, in both directions, with its close button
 * outside the viewport. Found by the P10.1 walk at 768px, which is exactly the
 * width nobody opens a dialog at by hand. Each size now climbs the ladder and
 * stops where the viewport stops it: 420 → 560 (`md`, 768px) → 760 (`lg`,
 * 1024px) → 960 (`xl`, 1280px), every step comfortably inside the breakpoint
 * that switches it on.
 */
export const dialogSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'sm:w-dialog-sm',
      md: 'sm:w-dialog-sm md:w-dialog-md',
      lg: 'sm:w-dialog-sm md:w-dialog-md lg:w-dialog-lg',
      xl: 'sm:w-dialog-sm md:w-dialog-md lg:w-dialog-lg xl:w-dialog-xl',
    },
  },
  defaultVariants: { size: 'md' },
});

export type DialogSizeVariants = VariantProps<typeof dialogSizeVariants>;

/**
 * Where a dialog sits, at each width.
 *
 * **From `sm` up** — a card centred with a transform rather than with
 * `start-1/2` alone, so nothing here depends on which way the document runs.
 *
 * **Below `sm`** — a **bottom sheet**: full-bleed, docked to the bottom edge,
 * square along that edge, entering with a slide instead of a zoom. A centred
 * 343px card is the wrong shape on a phone twice over — the gutters are dead
 * space on the narrowest screen there is, and the thumb has to travel to the
 * middle of the display to reach a control. The previous `w-dialog-inset` token
 * (`calc(100vw - 32px)`) existed to paper over the first half of that and is
 * retired with it.
 *
 * Both centring transforms are neutralised below `sm`, and the `sm:` overrides
 * restate the RTL one: `rtl:translate-x-1/2` and a plain reset have equal
 * specificity, so whichever Tailwind emits last would win by source order rather
 * than by intent. Keeping the pair together on the same breakpoint is what stops
 * a sheet from sitting half a screen off in Arabic while being correct in
 * English — a failure invisible unless the phone width is walked in ar.
 *
 * `pb-…+safe-b` keeps the last action clear of the iOS home indicator; the token
 * is `0px` everywhere else, so nothing changes on a desktop browser.
 */
export const DIALOG_POSITION_CLASSES = [
  'fixed flex flex-col gap-lg border border-border bg-card p-xl shadow-xl',
  'transition-transform duration-fast ease-standard',
  // Phone: docked to the bottom edge, full width, no side gutters.
  'inset-x-0 bottom-0 w-full max-h-sheet-mobile rounded-lg rounded-b-none pb-[calc(theme(spacing.xl)+theme(spacing.safe-b))]',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom',
  // `sm` and up: a centred card again.
  'sm:inset-x-auto sm:bottom-auto sm:top-1/2 sm:start-1/2 sm:w-auto sm:max-h-dialog sm:rounded-b-lg sm:pb-xl',
  'sm:-translate-y-1/2 sm:-translate-x-1/2 sm:rtl:translate-x-1/2',
  'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
].join(' ');

/**
 * Containment — the guarantee, not the convention.
 *
 * A dialog WITH a `DialogBody` clips at its own border and scrolls inside that
 * body, which is what pins the header and the footer. A dialog WITHOUT one used
 * to do the first half and not the second: `overflow-hidden` with nothing
 * scrollable underneath simply **cut the content off**, silently, with no way to
 * reach the rest. That is how P9's role form lost its Save button and how the
 * confirmation dialog lost the end of its own warning.
 *
 * `:has()` closes it structurally: present a body and the content box is
 * `overflow-hidden`; omit one and the whole dialog scrolls. Nothing is ever
 * unreachable, whatever the caller composed.
 *
 * A dev-time invariant was considered and rejected — a bodyless dialog is
 * legitimate (a bare confirm is two rows and a pair of buttons), so the warning
 * would be false nearly every time it fired. And a `Children` scan cannot see a
 * body anyway: `DynamicFormModal` renders a `<form>` between the content and its
 * regions, which is exactly the caller most at risk. The CSS sees through it.
 */
export const DIALOG_CONTAINMENT_CLASSES = [
  'has-[[data-dialog-body]]:overflow-hidden',
  '[&:not(:has([data-dialog-body]))]:overflow-y-auto',
  '[&:not(:has([data-dialog-body]))]:overscroll-contain',
].join(' ');

/**
 * The close affordance, and why it is `sticky` rather than `absolute`.
 *
 * With a `DialogBody` the panel does not scroll, so either would look the same.
 * WITHOUT one the panel itself is the scroller — and an absolutely positioned
 * child of a scroll container scrolls with the content: the ✕ leaves the top of
 * a long bodyless dialog and never comes back. Escape and the backdrop still
 * dismiss it, but the visible way out disappears exactly when the operator is
 * furthest from the top (U0: nothing an overlay offers may become unreachable).
 *
 * A zero-height sticky row pinned to the top of the scrollport carries it in
 * both cases: `order-first` puts it above the header in the flex column, and
 * `h-0` keeps it from taking any height of its own. `pe-2xl` on
 * `DialogHeader`/`SheetHeader` is what keeps a long title from running under it.
 *
 * Two classes here are not cosmetic, and both were found by MEASURING the row
 * rather than by reading it:
 *
 *   items-start   without it `align-items` resolves to stretch, and a flex item
 *                 in a container with a DEFINITE height of zero is stretched to
 *                 zero: the button's box collapsed to 24×8 with its own icon
 *                 overflowing it, so the hover fill and the focus ring painted a
 *                 sliver behind the glyph. `items-start` lets it keep its
 *                 natural 24×24.
 *   -mb-lg        a zero-height item still consumes the column's `gap-lg`, so
 *                 the row silently pushed every title 16px down the panel. The
 *                 negative margin cancels exactly that gap; the header starts at
 *                 the padding edge again, which is what "costs no space" has to
 *                 mean to be true.
 */
export const OVERLAY_CLOSE_ROW_CLASSES =
  'sticky top-0 order-first z-sticky flex h-0 -mb-lg items-start justify-end';

export const OVERLAY_CLOSE_CLASSES = [
  'relative -top-sm rounded-sm p-xs text-muted-foreground transition-colors duration-fast ease-standard',
  'hover:bg-secondary hover:text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
].join(' ');

/** The backdrop, shared by `Dialog` and `Sheet` so the two never differ. */
export const OVERLAY_CLASSES =
  'fixed inset-0 z-modalBackdrop bg-overlay/60 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0';
